import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  Select,
  InputNumber,
  Card,
  Row,
  Col,
  Typography,
  Avatar,
} from "antd";
import { PhoneOutlined, UserOutlined } from "@ant-design/icons";
import useMentorProfile from "PlatForms/OrgLMS/Hooks/MentorHooks/useMentorProfile";
import { Country, State, City } from "country-state-city";
const { Text } = Typography;
const { Option } = Select;
const MentorProfileForm = () => {
  const {
    form,
    fileList,
    handleImageChange,
    beforeUpload,
    onFinish,
    imageUrl,
    languages,
    skills,
    submitting,
    loading,
  } = useMentorProfile();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);
  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    const relatedStates = State.getStatesOfCountry(value);
    setStates(relatedStates);
    setCities([]);
    form.setFieldsValue({ state: undefined, city: undefined });
  };
  const handleStateChange = (value) => {
    setSelectedState(value);
    const relatedCities = City.getCitiesOfState(selectedCountry, value);
    setCities(relatedCities);
    form.setFieldsValue({ city: undefined });
  };
  return (
    <Card title="Mentor Profile" className="card-professional">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          mentorSkills: [],
          softSkills: [],
          certifications: [],
          languagesSpoken: [],
          trainingModes: [],
        }}
      >
        {/* Profile Image Row */}
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="imageFile">
              <Upload
                name="imageFile"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={beforeUpload}
                onChange={handleImageChange}
                fileList={fileList}
                accept="image/*"
              >
                {imageUrl ? (
                  <Avatar src={imageUrl} size={110} style={{ border: "2px solid #eee" }} />
                ) : (
                  <Avatar
                    icon={<UserOutlined />}
                    size={110}
                    style={{ backgroundColor: "#f0f0f0" }}
                  />
                )}
              </Upload>
              <Text type="secondary">Click to change</Text>
            </Form.Item>
          </Col>
        </Row>
        {/* Basic Information Row */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: "Please input your first name!" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: "Please input your last name!" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Email" name="email">
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>
        {/* Contact and Experience Row */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="phoneNo"
              label="Phone Number"
              rules={[
                { required: true, message: "Please enter your phone number!" },
                {
                  pattern: /^\+?[0-9\s]*$/,
                  message: "Please enter only numbers and optional + prefix!",
                },
              ]}
            >
              <Input
                type="tel"
                prefix={<PhoneOutlined />}
                placeholder="+1234567890 or 03XXXXXXXXX"
                onKeyPress={(e) => {
                  // Only allow numbers, +, and backspace
                  if (!/[0-9+]/.test(e.key) && e.key !== "Backspace") {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  // Ensure only one + at the start
                  const value = e.target.value;
                  if ((value.match(/\+/g) || []).length > 1) {
                    e.target.value = value.replace(/\+/g, "");
                    if (value.startsWith("+")) {
                      e.target.value = "+" + e.target.value;
                    }
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Years of Experience" name="yearsOfExperience">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Mentorship Experience Level" name="mentorshipExperienceLevel">
              <Select>
                <Option value={1}>Beginner</Option>
                <Option value={2}>Intermediate</Option>
                <Option value={3}>Advanced</Option>
                <Option value={4}>Expert</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Skills Row */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Technical Skills" name="mentorSkills">
              <Select mode="tags" placeholder="Select or add skills" optionFilterProp="children">
                {skills.map((skill) => (
                  <Option key={skill.id} value={skill.name}>
                    {skill.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Soft Skills" name="softSkills">
              <Select mode="tags" placeholder="Add soft skills" tokenSeparators={[","]} />
            </Form.Item>
          </Col>
        </Row>

        {/* Certifications and Languages Row */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Certifications" name="certifications">
              <Select mode="tags" placeholder="Add certifications" tokenSeparators={[","]} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Languages Spoken" name="languagesSpoken">
              <Select mode="multiple" placeholder="Select languages">
                {languages.map((lang) => (
                  <Option key={lang.id} value={lang.id}>
                    {lang.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* Training Modes Row */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Training Modes" name="trainingModes">
              <Select mode="multiple" placeholder="Select training modes">
                <Option value={1}>Online</Option>
                <Option value={2}>One-on-One</Option>
                <Option value={3}>Group</Option>
                <Option value={4}>Video</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true, message: "Please select your country!" }]}
            >
              <Select
                showSearch
                placeholder="Select your country"
                optionFilterProp="children"
                onChange={handleCountryChange}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                options={countries.map((country) => ({
                  label: country.name,
                  value: country.isoCode,
                }))}
              />
            </Form.Item>
          </Col>
          {/* State */}
          <Col xs={24} md={8}>
            <Form.Item
              label="State"
              name="state"
              rules={[
                {
                  required: selectedCountry === "US",
                  message: "State is required when country is United States!",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Select your state"
                optionFilterProp="children"
                onChange={handleStateChange}
                disabled={!selectedCountry}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                options={states.map((state) => ({
                  label: state.name,
                  value: state.isoCode,
                }))}
              />
            </Form.Item>
          </Col>

          {/* City */}
          <Col xs={24} md={8}>
            <Form.Item
              label="City"
              name="city"
              rules={[{ required: true, message: "Please select your city!" }]}
            >
              <Select
                showSearch
                placeholder="Select your city"
                optionFilterProp="children"
                disabled={!selectedState}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                options={cities.map((city) => ({
                  label: city.name,
                  value: city.name,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* Submit Button */}
        <Row>
          <Col span={24} style={{ textAlign: "right" }}>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} size="large">
                Update Profile
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
export default MentorProfileForm;
